import React, { useState, useEffect } from 'react';
import { FaSearch, FaFilter, FaDownload, FaCheckCircle, FaExclamationCircle, FaHourglassHalf, FaTrash, FaPaperPlane } from 'react-icons/fa';
import axios from 'axios';
import { CSVLink } from "react-csv";
import { useToast } from '../../context/ToastContext';
import SkeletonLoader from '../../components/ui/SkeletonLoader';
import PageLayout from '../../components/ui/PageLayout';
import PageHeader from '../../components/ui/PageHeader';
import EmailCampaignsNav from '../../components/ui/EmailCampaignsNav';
import StatCard, { StatGrid } from '../../components/ui/StatCard';
import FilterCard from '../../components/ui/FilterCard';
import DataTable, { TableEmpty } from '../../components/ui/DataTable';

const CampaignLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const { success: toastSuccess, error: toastError } = useToast();

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/campaigns/logs/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setLogs(res.data.data);
      }
    } catch (err) {
      toastError('Failed to fetch logs');
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch =
      log.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (log.user?.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (log.campaign?.campaignName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.subject.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === 'all' || log.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent': return <FaCheckCircle className="text-green-500" />;
      case 'failed': return <FaExclamationCircle className="text-red-500" />;
      case 'pending': return <FaHourglassHalf className="text-yellow-500" />;
      default: return null;
    }
  };

  const csvData = filteredLogs.map(log => ({
    'Campaign': log.campaign?.campaignName || 'N/A',
    'User': log.user?.name || 'N/A',
    'Email': log.email,
    'Subject': log.subject,
    'Status': log.status,
    'Sent At': log.sentAt ? new Date(log.sentAt).toLocaleString() : 'N/A',
    'Error': log.error || ''
  }));

  const handleDeleteLog = async (id) => {
    if (!window.confirm('Delete this email log entry?')) return;
    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/campaigns/logs/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toastSuccess('Log deleted');
      setLogs(prev => prev.filter(l => l._id !== id));
    } catch (err) {
      toastError('Failed to delete log');
    }
  };

  const handleClearAllLogs = async () => {
    if (!window.confirm('Are you sure you want to clear ALL email logs? This action cannot be undone.')) return;
    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/campaigns/logs/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toastSuccess('All logs cleared');
      setLogs([]);
    } catch (err) {
      toastError('Failed to clear logs');
    }
  };

  return (
    <PageLayout>
      <PageHeader
        title="Email Campaigns"
        description="Monitor delivery status of all campaign emails"
        icon={FaPaperPlane}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            {logs.length > 0 && (
              <button
                onClick={handleClearAllLogs}
                className="flex items-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-medium transition-colors"
              >
                <FaTrash className="w-4 h-4" /> Clear All
              </button>
            )}
            <CSVLink
              data={csvData}
              filename={`Campaign_Logs_${new Date().toISOString().split('T')[0]}.csv`}
              className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-medium transition-colors"
            >
              <FaDownload className="w-4 h-4" /> Export CSV
            </CSVLink>
          </div>
        }
      />

      <EmailCampaignsNav />

      <StatGrid cols="grid-cols-1 sm:grid-cols-3">
        <StatCard
          label="Successfully Sent"
          value={logs.filter(l => l.status === 'sent').length}
          icon={FaCheckCircle}
          iconBg="bg-green-100 dark:bg-green-900/30"
          iconColor="text-green-600 dark:text-green-400"
          valueColor="text-green-600"
        />
        <StatCard
          label="Failed Delivery"
          value={logs.filter(l => l.status === 'failed').length}
          icon={FaExclamationCircle}
          iconBg="bg-red-100 dark:bg-red-900/30"
          iconColor="text-red-600 dark:text-red-400"
          valueColor="text-red-600"
        />
        <StatCard
          label="Still Pending"
          value={logs.filter(l => l.status === 'pending').length}
          icon={FaHourglassHalf}
          iconBg="bg-yellow-100 dark:bg-yellow-900/30"
          iconColor="text-yellow-600 dark:text-yellow-400"
          valueColor="text-yellow-600"
        />
      </StatGrid>

      <FilterCard>
        <div className="flex flex-col md:flex-row gap-4 items-center">
          <div className="relative flex-1 w-full">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search logs by user, email, campaign, or subject..."
              className="input-modern pl-11 py-2.5 text-sm w-full"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2 w-full md:w-auto">
            <FaFilter className="text-gray-400 w-4 h-4 shrink-0" />
            <select
              className="input-modern py-2.5 text-sm font-medium w-full md:w-48"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>
      </FilterCard>

      {loading ? (
        <SkeletonLoader variant="table" rows={8} cols={7} />
      ) : (
        <DataTable>
          <table className="w-full">
            <thead>
              <tr className="bg-gradient-to-r from-primary-600 to-primary-700 text-white">
                <th className="px-4 py-4 text-left text-sm font-semibold">Recipient</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Campaign</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Subject</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Status</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Sent At</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Error</th>
                <th className="px-4 py-4 text-right text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
              {filteredLogs.length === 0 ? (
                <TableEmpty colSpan={7} preset="search" title="No Logs Found" description="No campaign logs matched your filters." />
              ) : (
                filteredLogs.map(log => (
                  <tr key={log._id} className="hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">{log.user?.name || 'N/A'}</span>
                        <span className="text-xs text-gray-500">{log.email}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">{log.campaign?.campaignName || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm max-w-xs truncate">{log.subject}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 text-xs font-semibold uppercase">
                        {getStatusIcon(log.status)} {log.status}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {log.sentAt ? new Date(log.sentAt).toLocaleString() : '---'}
                    </td>
                    <td className="px-4 py-3 text-xs text-red-500 max-w-xs truncate">
                      {log.error || 'None'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleDeleteLog(log._id)}
                        className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        title="Delete Log"
                      >
                        <FaTrash className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </DataTable>
      )}
    </PageLayout>
  );
};

export default CampaignLogs;
